import axios from 'axios';
import dayjs from 'dayjs';
import 'moment/locale/fr';
import moment from 'moment/moment';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import MapItem from '../../components/MapItem';
import McDateRangePicker from '../../components/McDateRangePicker';
import ReportAnnonceForm from '../../components/ReportAnnonceForm';
import ReviewItemVehicle from '../../components/ReviewItemVehicle';
import env from '../../config/env';
import jsonConfig from '../../config/index.json';
import { isOverlapWithUnavailabilities } from '../../utils/unavailability';
import './mc-vehicle-detail.css';

moment.locale('fr');

const PREAVIS = [1, 2, 3, 6, 12, 24, 48];
const TAMPO = [3, 4, 5, 6, 7, 8, 12, 24, 48, 36];
const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const RATING_ROWS = [
  { value: '5', label: 'Excellent' },
  { value: '4', label: 'Très bien' },
  { value: '3', label: 'Moyen' },
  { value: '2', label: 'Passable' },
  { value: '1', label: 'Mauvais' },
];

const CANCEL_POLICIES = {
  flexible: {
    label: 'Flexible',
    tone: 'success',
    icon: 'bi-check-circle-fill',
    rules: [
      {
        tone: 'ok',
        icon: 'bi-check-circle',
        text: "Annulation gratuite jusqu'à 24h avant le début",
      },
      { tone: 'mid', icon: 'bi-dash-circle', text: '50% remboursé si annulation dans les 24h' },
    ],
  },
  moderate: {
    label: 'Modérée',
    tone: 'warning',
    icon: 'bi-dash-circle-fill',
    rules: [
      { tone: 'ok', icon: 'bi-check-circle', text: "Annulation gratuite jusqu'à 5 jours avant" },
      { tone: 'mid', icon: 'bi-dash-circle', text: '50% remboursé entre 1 et 5 jours avant' },
      { tone: 'no', icon: 'bi-x-circle', text: 'Non remboursable dans les 24h' },
    ],
  },
  strict: {
    label: 'Stricte',
    tone: 'danger',
    icon: 'bi-x-circle-fill',
    rules: [
      { tone: 'ok', icon: 'bi-check-circle', text: "Annulation gratuite jusqu'à 14 jours avant" },
      { tone: 'no', icon: 'bi-x-circle', text: 'Non remboursable dans les 14 jours' },
    ],
  },
};

const fraisVoyage = 8;
const TVA = 2;

const getDayOfWeek = (date) => {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
};

// Intersecte la fenêtre de l'annonce avec la disponibilité de l'hôte
const intersectWindow = (annonceStart, annonceEnd, hostStart, hostEnd) => {
  if (!hostStart || !hostEnd) return [annonceStart, annonceEnd];
  const maxStart = annonceStart > hostStart ? annonceStart : hostStart;
  const minEnd = annonceEnd < hostEnd ? annonceEnd : hostEnd;
  if (maxStart >= minEnd) return [annonceStart, annonceEnd];
  return [maxStart, minEnd];
};

// Options d'heure filtrées par créneau (format "HH:MM")
const generateHourOptions = (minTime = '00:00', maxTime = '23:30') => {
  const options = [];
  const [minH, minM] = minTime.split(':').map(Number);
  const [maxH, maxM] = maxTime.split(':').map(Number);
  const minTotal = minH * 60 + minM;
  const maxTotal = maxH * 60 + maxM;

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const total = hour * 60 + minute;
      if (total < minTotal || total > maxTotal) continue;
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(
        <option key={timeString} value={timeString}>
          {timeString}
        </option>
      );
    }
  }
  return options;
};

function calculerPrixTotal(Availabilities, DateIn, DateOut) {
  let prixTotal = 0;
  if (!DateIn || !DateOut) return 0;

  let dateActuelle = new Date(DateIn);
  const dateFin = new Date(DateOut);
  const hasAvailabilities = Array.isArray(Availabilities) && Availabilities.length > 0;

  while (dateActuelle <= dateFin) {
    const jourSemaine = getDayOfWeek(dateActuelle);
    const prixJour =
      hasAvailabilities &&
      Availabilities[jourSemaine] &&
      typeof Availabilities[jourSemaine].Default_price === 'number'
        ? Availabilities[jourSemaine].Default_price
        : 0;
    prixTotal += prixJour;
    dateActuelle.setDate(dateActuelle.getDate() + 1);
  }
  return prixTotal;
}

function Gallery({ images }) {
  const [index, setIndex] = useState(0);
  const total = images.length;
  const current = Math.min(index, Math.max(total - 1, 0));

  const go = (delta) => setIndex((i) => (i + delta + total) % total);

  return (
    <div>
      <div className="mc-vd-gallery">
        <div className="mc-vd-stage">
          {total > 0 ? (
            <img src={images[current]} alt={`Photo ${current + 1} sur ${total}`} />
          ) : (
            <div className="mc-vd-stage-empty" aria-hidden="true">
              <i className="bi bi-image"></i>
            </div>
          )}
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              className="mc-vd-nav mc-vd-nav--prev"
              onClick={() => go(-1)}
              aria-label="Photo précédente"
            >
              <i className="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="mc-vd-nav mc-vd-nav--next"
              onClick={() => go(1)}
              aria-label="Photo suivante"
            >
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <span className="mc-vd-count">
              {current + 1} / {total}
            </span>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="mc-vd-thumbs" role="tablist" aria-label="Photos du véhicule">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              role="tab"
              aria-selected={i === current}
              aria-label={`Voir la photo ${i + 1}`}
              className={`mc-vd-thumb${i === current ? ' is-active' : ''}`}
              onClick={() => setIndex(i)}
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VoitureProfilePage() {
  const { annonceid } = useParams();
  const [Reviews, setReviews] = useState([]);
  const [Message, setMessage] = useState('');
  const [Message2, setMessage2] = useState('');
  const [minReservation, setMinReservation] = useState(false);
  const [maxReservation, setMaxReservation] = useState(false);

  const [DateIn, setDateIn] = useState(dayjs().add(3, 'day').format('YYYY-MM-DD'));
  const [DateOut, setDateOut] = useState(dayjs().add(8, 'day').format('YYYY-MM-DD'));
  const [CalcluatedPrice, setCalcluatedPrice] = useState(0);
  const [NombreJours, setNombreJours] = useState(0);
  const [IsUnaivalable, setIsUnaivalable] = useState(false);
  const [vehicleAvailability, setVehicleAvailability] = useState(null); // null=non vérifié, true=dispo, false=indispo
  const [availabilityConflict, setAvailabilityConflict] = useState(null);

  const [Discounts, setDiscounts] = useState(null);
  const [ReductionPrix, setReductionPrix] = useState(0);

  const startDateInit = dayjs().add(3, 'day');
  const endDateInit = startDateInit.add(5, 'day');
  const [selectedDates, setSelectedDates] = useState([startDateInit, endDateInit]);

  const [personnalisedMessage, setPersonnalisedMessage] = useState('');

  const [annonces, setAnnonces] = useState(null);
  const [vehicleImages, setVehicleImages] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [DeliveryLocation, setDeliveryLocation] = useState(null);
  const [Availabilities, setAvailabilities] = useState(null);
  const [VehicleUnavailabilities, setVehicleUnavailabilities] = useState([]);
  const [IsInstanne, setIsInstanne] = useState(true);
  const [preavis, setPreavis] = useState(0);
  const [tampo, setTampo] = useState(0);

  const me = window.localStorage.getItem('me');
  const user = JSON.parse(me);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    if (!user?.id || !annonces?.vehiculeAnnonceId || annonces?.vehiculeAnnonceId === 1) return;
    axios
      .get(`${env.baseUrlApi}/api/favorites/check/${user.id}/${annonces?.vehiculeAnnonceId}`)
      .then((r) => setIsFavorited(r.data.favorited))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annonces?.vehiculeAnnonceId]);

  const handleToggleFavorite = async () => {
    if (!user?.id) return (window.location.href = '/signin');
    try {
      const { data } = await axios.post(`${env.baseUrlApi}/api/favorites/toggle`, {
        userId: user.id,
        vehiculeAnnonceId: annonces?.vehiculeAnnonceId,
      });
      setIsFavorited(data.favorited);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let fetchAnnonce = async () => {
      try {
        const response = await axios.get(
          `${env.baseUrlApi}/vehicleAnnonces/getannoncebyid/` + annonceid
        );
        if (response.data) {
          setAnnonces(response.data);
          setVehicleImages(
            (response.data.Vehicle?.images || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          );
        }

        const responseOption = await axios.get(
          `${env.baseUrlApi}/vehicleOptionRecords/` + response.data.Vehicle.id
        );
        if (response.data) {
          setVehicleOptions(responseOption.data);
        }

        const DeliverLoc = await axios.get(
          `${env.baseUrlApi}/deliverylocation/getannoncebyid/` + annonceid
        );
        if (DeliverLoc.data) {
          setDeliveryLocation(DeliverLoc.data[0].addressEmplacementDelivery);
        }

        const avai = await axios.get(
          `${env.baseUrlApi}/api/availabilities/getbyvehiculeid/` + response.data.Vehicle.id
        );
        if (avai.data) {
          setAvailabilities(avai.data);
        }

        const discountsresp = await axios.get(
          `${env.baseUrlApi}/api/discounts/getbyvehiculeid/` + response.data.Vehicle.id
        );
        if (discountsresp.data) {
          setDiscounts(discountsresp.data);
        }

        const unavailResp = await axios.get(
          `${env.baseUrlApi}/api/unavailabilities/getbyvehiculeid/` + response.data.Vehicle.id
        );
        if (Array.isArray(unavailResp.data)) {
          setVehicleUnavailabilities(unavailResp.data);
        }

        const resp = await axios.get(
          `${env.baseUrlApi}/api/reservationpreferences/getbyvehiculeid/` + response.data.Vehicle.id
        );

        setPreavis(resp.data.home_preavis);
        setTampo(resp.data.HomePeriodTempo);

        if (resp.data.reservationsInstantanne == true) {
          setIsInstanne(true);
        } else {
          const startDate = new Date(DateIn);
          const today = new Date();

          if (
            startDate.getDate() == today.getDate() &&
            startDate.getMonth() == today.getMonth() &&
            startDate.getFullYear() == today.getFullYear()
          ) {
            setIsInstanne(false);
          }
        }

        const minVoyages = ['0', '2', '3', '4', '5'];
        const maxMoyages = ['5', '7', '14', '30', '60', '0'];

        var diffe = new Date(DateOut).getTime() - new Date(DateIn).getTime();
        var nombreJours = diffe / (1000 * 3600 * 24);

        if (resp.data.home_minDureeVoyage != '0') {
          if (nombreJours < minVoyages[resp.data.home_minDureeVoyage]) {
            setMessage2(
              'Le nombre de jours doit etre minimum ' +
                minVoyages[resp.data.home_minDureeVoyage] +
                ' jours.'
            );
            setMinReservation(true);
          } else {
            setMessage2('');
            setMinReservation(false);
          }
        }

        if (resp.data.home_maxDureeVoyage != '0') {
          if (nombreJours > maxMoyages[resp.data.home_maxDureeVoyage]) {
            setMessage(
              'Le nombre de jours ne doit pas depassé ' +
                maxMoyages[resp.data.home_maxDureeVoyage] +
                ' jours.'
            );
            setMaxReservation(true);
          } else {
            setMessage('');
            setMaxReservation(false); // corrigé : réinitialisait minReservation par erreur
          }
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchAnnonce();
  }, [annonceid, DateIn, DateOut]);

  useEffect(() => {
    if (!annonces?.Vehicle?.id) return;
    let fetchReviews = async () => {
      try {
        const response = await axios.get(
          `${env.baseUrlApi}/api/reviews/getbyvehiculeId/` + annonces?.Vehicle?.id
        );
        if (response.data) {
          const sortedData = [...response.data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setReviews(sortedData);
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchReviews();
  }, [annonces?.Vehicle?.id]);

  const [selectedValue, setSelectedValue] = useState('09:00');
  const [selectedValue2, setSelectedValue2] = useState('18:00');

  // Disponibilités de l'hôte pour les dates sélectionnées
  const [hostAvailPickup, setHostAvailPickup] = useState(null);
  const [hostAvailReturn, setHostAvailReturn] = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState([]);

  const hostId = annonces?.Vehicle?.UserProfile?.id || annonces?.Vehicle?.userId;

  useEffect(() => {
    if (!hostId || annonces?.vehiculeAnnonceId === 1) return;
    axios
      .get(`${env.baseUrlApi}/api/hoteavailabilities/${hostId}/weekly?mode=0`)
      .then((r) => Array.isArray(r.data) && setWeeklySchedule(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annonces?.vehiculeAnnonceId]);

  useEffect(() => {
    if (!DateIn || !hostId) return;
    axios
      .get(`${env.baseUrlApi}/api/hoteavailabilities/${hostId}/for-date?date=${DateIn}&mode=0`)
      .then((r) => setHostAvailPickup(r.data))
      .catch(() => setHostAvailPickup(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DateIn, annonces?.vehiculeAnnonceId]);

  useEffect(() => {
    if (!DateOut || !hostId) return;
    axios
      .get(`${env.baseUrlApi}/api/hoteavailabilities/${hostId}/for-date?date=${DateOut}&mode=0`)
      .then((r) => setHostAvailReturn(r.data))
      .catch(() => setHostAvailReturn(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DateOut, annonces?.vehiculeAnnonceId]);

  const pickupWindow = useMemo(
    () =>
      intersectWindow(
        annonces?.pickupTimeStart || '00:00',
        annonces?.pickupTimeEnd || '23:30',
        hostAvailPickup?.startTime,
        hostAvailPickup?.endTime
      ),
    [annonces?.pickupTimeStart, annonces?.pickupTimeEnd, hostAvailPickup]
  );

  const returnWindow = useMemo(
    () =>
      intersectWindow(
        annonces?.returnTimeStart || '00:00',
        annonces?.returnTimeEnd || '23:30',
        hostAvailReturn?.startTime,
        hostAvailReturn?.endTime
      ),
    [annonces?.returnTimeStart, annonces?.returnTimeEnd, hostAvailReturn]
  );

  // Auto-initialise les heures dès que l'annonce est chargée
  useEffect(() => {
    if (pickupWindow[0]) setSelectedValue((v) => (v === '09:00' || !v ? pickupWindow[0] : v));
  }, [pickupWindow[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (returnWindow[0]) setSelectedValue2((v) => (v === '18:00' || !v ? returnWindow[0] : v));
  }, [returnWindow[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (newDates) => {
    setSelectedDates(newDates);

    let date1 = new Date(newDates[0]?.$d);
    let date2 = new Date(newDates[1]?.$d);

    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;

    setDateIn(fmt(date1));
    setDateOut(fmt(date2));
    // Reset la disponibilité dès que les dates changent
    setVehicleAvailability(null);
    setAvailabilityConflict(null);
  };

  // Vérification disponibilité véhicule (réservation active en chevauchement)
  useEffect(() => {
    const vehicleId = annonces?.Vehicle?.id;
    if (!DateIn || !DateOut || !vehicleId || vehicleId === 1) return;
    let cancelled = false;
    axios
      .get(
        `${env.baseUrlApi}/api/reservations/check-availability/${vehicleId}?startDate=${DateIn}&endDate=${DateOut}`
      )
      .then(({ data }) => {
        if (cancelled) return;
        setVehicleAvailability(data.available);
        setAvailabilityConflict(data.conflict ?? null);
      })
      .catch(() => {
        if (!cancelled) setVehicleAvailability(true); // fail-open : ne pas bloquer si erreur réseau
      });
    return () => {
      cancelled = true;
    };
  }, [DateIn, DateOut, annonces?.Vehicle?.id]);

  useEffect(() => {
    if (DateIn && DateOut) {
      var difference = new Date(DateOut).getTime() - new Date(DateIn).getTime();
      if (isNaN(difference)) return;
      var nombre_de_jours = difference / (1000 * 3600 * 24);
      setNombreJours(nombre_de_jours);
      const pricePerDay =
        Array.isArray(Availabilities) && Availabilities.length > 0
          ? Availabilities[0].Default_price
          : parseFloat(annonces?.reservationPrice || 0);

      // ── Calcul réduction intelligente ──
      const getDiscount = (duration) =>
        parseFloat(
          Discounts?.find((d) => d.reservation_duration === duration)?.pourcentage_reduction || 0
        );

      let durationDiscount = 0;
      if (nombre_de_jours >= 60) durationDiscount = getDiscount('4');
      else if (nombre_de_jours >= 30) durationDiscount = getDiscount('3');
      else if (nombre_de_jours >= 14) durationDiscount = getDiscount('2');
      else if (nombre_de_jours >= 7) durationDiscount = getDiscount('1');
      else if (nombre_de_jours >= 3) durationDiscount = getDiscount('0');

      let bonusDiscount = 0;
      if (Discounts && Discounts.length > 0) {
        const startDate = new Date(DateIn);
        const now = new Date();
        startDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        const diffDays = (startDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
        if (diffDays === 0) bonusDiscount += getDiscount('5'); // instantanée (même jour)
        if (diffDays >= 7) bonusDiscount += getDiscount('6'); // anticipée (≥ 7 jours)
      }

      const totalReduction = Math.min(durationDiscount + bonusDiscount, 100);
      setReductionPrix(totalReduction);

      const basePrice = nombre_de_jours * pricePerDay;
      setCalcluatedPrice(basePrice * (1 - totalReduction / 100));
    }

    const startDate = new Date(DateIn);
    const today = new Date();

    if (
      startDate.getDate() == today.getDate() &&
      startDate.getMonth() == today.getMonth() &&
      startDate.getFullYear() == today.getFullYear()
    ) {
      setIsInstanne(false);
    } else setIsInstanne(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DateIn, DateOut]);

  useEffect(() => {
    if (!DateIn || !DateOut) return;
    setIsUnaivalable(isOverlapWithUnavailabilities(VehicleUnavailabilities, DateIn, DateOut));
  }, [DateIn, DateOut, VehicleUnavailabilities]);

  function handleContinuer() {
    const effectiveStartTime = selectedValue || pickupWindow[0] || '09:00';
    const effectiveEndTime = selectedValue2 || returnWindow[0] || '18:00';

    window.location.href =
      `${env.apiUrl}/onboarding?action=CHECKOUT` +
      '&endDate=' +
      DateOut +
      '&endTime=' +
      effectiveEndTime +
      '&startDate=' +
      DateIn +
      '&startTime=' +
      effectiveStartTime +
      '&reservationLocationType=HOME' +
      '&vehicleId=' +
      annonces?.Vehicle?.id +
      '&NombreJour=' +
      NombreJours +
      '&priceU=' +
      (Array.isArray(Availabilities) && Availabilities.length > 0
        ? Availabilities[0].Default_price
        : parseFloat(annonces?.reservationPrice || 0)) +
      '&fraisVoyage=' +
      fraisVoyage +
      '&Tva=' +
      TVA +
      '&Reduction=' +
      ReductionPrix +
      '&FreeMinDistance=' +
      annonces?.distanceOutMin +
      '&annonceId=' +
      annonces?.vehiculeAnnonceId +
      '&preavis=' +
      PREAVIS[preavis] +
      '&tampo=' +
      TAMPO[tampo];
  }

  const [selectedFilters, setSelectedFilters] = useState([]);

  // Statistiques d'avis (calculées une seule fois par avis)
  const { ratingsCount, averageRating, NBReviews } = useMemo(() => {
    const count = {};
    let total = 0;
    Reviews.forEach((review) => {
      count[review.rating] = (count[review.rating] || 0) + 1;
      total += parseInt(review.rating);
    });
    return {
      ratingsCount: count,
      averageRating: Reviews.length > 0 ? total / Reviews.length : 0,
      NBReviews: Reviews.length,
    };
  }, [Reviews]);

  const handleFilterChange = (filter) => {
    setSelectedFilters((current) =>
      current.includes(filter) ? current.filter((item) => item !== filter) : [...current, filter]
    );
  };

  const filteredReviews =
    selectedFilters.length === 0
      ? Reviews
      : Reviews.filter((review) => selectedFilters.includes(review.rating.toString()));

  const calculateRefundDate = () => {
    const now = new Date();
    const refundDate = new Date(now);
    refundDate.setDate(now.getDate() + 1);
    refundDate.setHours(10, 0, 0, 0);
    return refundDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Prix effectif : Availabilities en priorité, sinon reservationPrice de l'annonce
  const effectivePrice =
    parseFloat(Availabilities?.[0]?.Default_price) || parseFloat(annonces?.reservationPrice) || 0;

  // Manquant uniquement si aucune source ne donne un prix > 0 ET que les Availabilities sont chargées
  const defaultPriceMissing = Array.isArray(Availabilities) && effectivePrice <= 0;

  const isDisabled =
    IsUnaivalable ||
    minReservation ||
    maxReservation ||
    defaultPriceMissing ||
    vehicleAvailability === false;

  const isOwner = Boolean(user?.id) && user.id == annonces?.Vehicle?.userId;

  const blockReasons = [
    IsUnaivalable && 'Période indisponible (indisponibilité hôte)',
    minReservation && (Message2 || 'Durée minimum non atteinte'),
    maxReservation && (Message || 'Durée maximum dépassée'),
    defaultPriceMissing && 'Prix non configuré sur cette annonce',
    vehicleAvailability === false && 'Véhicule déjà réservé sur ces dates',
  ].filter(Boolean);

  const canBook = IsInstanne && !isOwner && !isDisabled;
  const totalPrice = calculerPrixTotal(Availabilities, DateIn, DateOut);

  const host = annonces?.Vehicle?.UserProfile;
  const hostInitials = `${host?.firstName?.[0] ?? ''}${host?.lastName?.[0] ?? ''}`.toUpperCase();
  const policy = CANCEL_POLICIES[annonces?.annulationPolicy] || CANCEL_POLICIES.moderate;

  const [pickupMin, pickupMax] = pickupWindow;
  const [returnMin, returnMax] = returnWindow;

  return (
    <main className="mc-home mc-vd-page">
      <div className="container mc-container">
        <div className="mc-vd-layout">
          {/* ════ Colonne principale ════ */}
          <div className="mc-vd-main">
            <Gallery images={vehicleImages} />

            {/* Titre + caractéristiques */}
            <section className="mc-vd-card">
              <div className="mc-vd-head">
                <div>
                  <h1 className="mc-vd-title">{annonces?.Vehicle?.description}</h1>
                  <span className="mc-vd-rating">
                    <i className="bi bi-star-fill" aria-hidden="true"></i>
                    {NBReviews > 0 ? (
                      <>
                        <strong>{averageRating.toFixed(1)}</strong> ({NBReviews} avis)
                      </>
                    ) : (
                      'Pas encore d’avis'
                    )}
                  </span>
                </div>

                <button
                  type="button"
                  className={`mc-vd-fav${isFavorited ? ' is-on' : ''}`}
                  onClick={handleToggleFavorite}
                  aria-pressed={isFavorited}
                  aria-label={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  title={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                >
                  <i
                    className={`bi ${isFavorited ? 'bi-heart-fill' : 'bi-heart'}`}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>

              <div className="mc-vd-specs">
                <div className="mc-vd-spec">
                  <i className="bi bi-fuel-pump" aria-hidden="true"></i>
                  {jsonConfig.carburant[annonces?.Vehicle?.carburantType]}
                </div>
                <div className="mc-vd-spec">
                  <i className="bi bi-people" aria-hidden="true"></i>
                  {jsonConfig.siegeNumber[annonces?.Vehicle?.siegeNumber]} sièges
                </div>
                <div className="mc-vd-spec">
                  <i className="bi bi-door-closed" aria-hidden="true"></i>
                  {jsonConfig.porteNumber[annonces?.Vehicle?.porteNumber]} portes
                </div>
              </div>
            </section>

            {/* Hôte */}
            <section className="mc-vd-card">
              <h2 className="mc-vd-section-title">Votre hôte</h2>
              <div className="mc-vd-host">
                <div className="mc-vd-host-avatar">
                  {host?.profile_url ? (
                    <img src={host.profile_url} alt="" />
                  ) : (
                    hostInitials || <i className="bi bi-person" aria-hidden="true"></i>
                  )}
                </div>
                <div>
                  <p className="mc-vd-host-name">
                    {host?.firstName} {host?.lastName}.
                  </p>
                  <p className="mc-vd-host-meta">
                    <span className="is-ok">
                      Membre depuis le {moment(host?.createdAt).calendar()}
                    </span>
                  </p>
                  <p className="mc-vd-host-meta">Répond habituellement en 1 minute</p>
                </div>
              </div>

              {annonces?.Vehicle?.description && (
                <p className="mc-vd-desc">{annonces.Vehicle.description}</p>
              )}

              {vehicleOptions && vehicleOptions.length > 0 && (
                <div className="mc-vd-chips">
                  {vehicleOptions.map((option, index) => (
                    <span key={index} className="mc-vd-chip">
                      {option.VehicleOption.name}
                    </span>
                  ))}
                </div>
              )}
            </section>

            {/* Avis */}
            <section className="mc-vd-card">
              <div className="mc-vd-reviews-head">
                <h2 className="mc-vd-section-title">Évaluations et commentaires</h2>
                <span className="mc-vd-score">
                  <strong>{averageRating.toFixed(2)}</strong> · {NBReviews || 'aucune'}{' '}
                  évaluation(s)
                </span>
              </div>

              <div className="mc-vd-bars">
                {RATING_ROWS.map((row) => {
                  const pct = ratingsCount[row.value]
                    ? (ratingsCount[row.value] / Reviews.length) * 100
                    : 0;
                  const id = `mc-vd-rate-${row.value}`;
                  return (
                    <div className="mc-vd-bar" key={row.value}>
                      <label htmlFor={id}>
                        <input
                          id={id}
                          type="checkbox"
                          value={row.value}
                          checked={selectedFilters.includes(row.value)}
                          onChange={() => handleFilterChange(row.value)}
                        />
                        {row.label}
                      </label>
                      <div
                        className="mc-vd-bar-track"
                        role="progressbar"
                        aria-label={row.label}
                        aria-valuenow={Math.round(pct)}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        <div className="mc-vd-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="mc-vd-bar-pct">{pct ? `${Math.round(pct)}%` : '—'}</span>
                    </div>
                  );
                })}
              </div>

              {filteredReviews.length > 0 ? (
                <div>
                  {filteredReviews.map((conv) => (
                    <ReviewItemVehicle annuler={true} data={conv} key={conv.id}></ReviewItemVehicle>
                  ))}
                </div>
              ) : (
                <div className="mc-vd-empty">Il n’y a pas assez d’évaluations</div>
              )}
            </section>
          </div>

          {/* ════ Réservation ════ */}
          <aside className="mc-vd-aside" aria-label="Réservation">
            <div className="mc-vd-card">
              <div className="mc-vd-price">
                <p className="mc-vd-price-total">
                  {DateIn && DateOut && (
                    <>
                      {totalPrice}€ <small>au total</small>
                    </>
                  )}
                </p>
                <button
                  type="button"
                  className="mc-vd-link"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasExample"
                  aria-controls="offcanvasExample"
                >
                  Voir les prix en détail
                </button>
              </div>

              <hr className="mc-vd-divider" />

              <div className="mc-vd-picker">
                <McDateRangePicker value={selectedDates} onChange={handleDateChange} />
              </div>

              {/* Heures prise en charge / retour */}
              <div className="mc-vd-times">
                <div>
                  <label className="mc-field-label" htmlFor="mc-vd-pickup">
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true"></i>
                    Prise en charge
                    <em>
                      {pickupMin} – {pickupMax}
                    </em>
                    {hostAvailPickup?.source === 'custom' && (
                      <span className="mc-vd-tag">personnalisé</span>
                    )}
                  </label>
                  <select
                    id="mc-vd-pickup"
                    className="mc-select"
                    value={selectedValue}
                    onChange={(e) => setSelectedValue(e.target.value)}
                  >
                    {generateHourOptions(pickupMin, pickupMax)}
                  </select>
                </div>
                <div>
                  <label className="mc-field-label" htmlFor="mc-vd-return">
                    <i className="bi bi-box-arrow-left" aria-hidden="true"></i>
                    Retour
                    <em>
                      {returnMin} – {returnMax}
                    </em>
                    {hostAvailReturn?.source === 'custom' && (
                      <span className="mc-vd-tag">personnalisé</span>
                    )}
                  </label>
                  <select
                    id="mc-vd-return"
                    className="mc-select"
                    value={selectedValue2}
                    onChange={(e) => setSelectedValue2(e.target.value)}
                  >
                    {generateHourOptions(returnMin, returnMax)}
                  </select>
                </div>
              </div>

              {/* Planning hebdomadaire de l'hôte */}
              {weeklySchedule.length > 0 && (
                <div className="mc-vd-week">
                  <p className="mc-vd-sub">
                    <i className="bi bi-clock" aria-hidden="true"></i> Disponibilités de l'hôte
                  </p>
                  <div className="mc-vd-week-days">
                    {WEEKDAY_LABELS.map((label, i) => {
                      const slot = weeklySchedule.find((s) => s.Weekday === String(i));
                      const avail = slot?.alwaysAvailable;
                      const hasSlot = slot && slot.startTime && slot.endTime;
                      return (
                        <div key={i} className={`mc-vd-day${hasSlot || avail ? ' is-open' : ''}`}>
                          <strong>{label}</strong>
                          <span>
                            {avail
                              ? 'Toujours'
                              : hasSlot
                                ? `${(slot.startTime || '').slice(0, 5)}–${(slot.endTime || '').slice(0, 5)}`
                                : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <hr className="mc-vd-divider" />

              <div className="mc-vd-place">
                <i className="bi bi-geo-alt" aria-hidden="true"></i>
                <div>
                  <p>
                    <strong>Lieu de prise en charge et de retour</strong>
                  </p>
                  <p>{DeliveryLocation}</p>
                  <small>
                    Nous vous enverrons l’adresse complète dès que votre trajet sera réservé.
                  </small>
                </div>
              </div>

              <hr className="mc-vd-divider" />

              {/* Réduction */}
              {!IsUnaivalable && ReductionPrix > 0 && (
                <div className="mc-vd-note mc-vd-note--ok">
                  <i className="bi bi-tag-fill" aria-hidden="true"></i>
                  <div>
                    <strong>Réduction appliquée : -{ReductionPrix}%</strong>
                    <div>
                      {NombreJours >= 60 && 'Tarif 2 mois ou plus'}
                      {NombreJours >= 30 && NombreJours < 60 && 'Tarif 30 jours ou plus'}
                      {NombreJours >= 14 && NombreJours < 30 && 'Tarif 2 semaines ou plus'}
                      {NombreJours >= 7 && NombreJours < 14 && 'Tarif 7 jours ou plus'}
                      {NombreJours >= 3 && NombreJours < 7 && 'Tarif 3 jours ou plus'}
                    </div>
                    <strong>Prix final : {CalcluatedPrice.toFixed(2)} €</strong>
                  </div>
                </div>
              )}

              {/* Disponibilité */}
              {!isOwner && vehicleAvailability === null && DateIn && DateOut && (
                <div className="mc-vd-status" role="status">
                  <span className="mc-vd-spin" aria-hidden="true"></span>
                  Vérification des disponibilités…
                </div>
              )}

              {!isOwner && vehicleAvailability === false && (
                <div className="mc-vd-note mc-vd-note--danger" role="alert">
                  <i className="bi bi-slash-circle-fill" aria-hidden="true"></i>
                  <div>
                    <strong>Véhicule indisponible</strong> pour ces dates.
                    {availabilityConflict && (
                      <div>
                        Déjà réservé du{' '}
                        <strong>
                          {new Date(availabilityConflict.startDate).toLocaleDateString('fr-FR')}
                        </strong>{' '}
                        au{' '}
                        <strong>
                          {new Date(availabilityConflict.endDate).toLocaleDateString('fr-FR')}
                        </strong>
                        .
                      </div>
                    )}
                    <div>Veuillez sélectionner d'autres dates.</div>
                  </div>
                </div>
              )}

              {!isOwner && vehicleAvailability === true && DateIn && DateOut && (
                <div className="mc-vd-status is-ok" role="status">
                  <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                  Véhicule disponible pour ces dates.
                </div>
              )}

              {/* Raisons de blocage */}
              {!isOwner && blockReasons.length > 0 && (
                <div className="mc-vd-note" role="alert">
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                  <div>
                    <strong>Réservation impossible :</strong>
                    <ul>
                      {blockReasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {!IsInstanne && (
                <div className="mc-vd-note">
                  <i className="bi bi-info-circle" aria-hidden="true"></i>
                  <span>Ce propriétaire n'autorise pas les réservations instantanées.</span>
                </div>
              )}

              {isOwner ? (
                <p className="mc-vd-own">Cette voiture m'appartient</p>
              ) : (
                IsInstanne && (
                  <button
                    type="button"
                    className="mc-btn mc-btn--primary mc-btn--block"
                    disabled={isDisabled}
                    onClick={handleContinuer}
                  >
                    Continuer
                  </button>
                )
              )}

              <hr className="mc-vd-divider" />

              <div className="mc-vd-facts">
                <div className="mc-vd-fact">
                  <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                  <div>
                    <p>
                      <strong>Annulation gratuite</strong>
                    </p>
                    <p className="mc-vd-muted">
                      Remboursement complet avant le {calculateRefundDate()} 10:00
                    </p>
                  </div>
                </div>
                <div className="mc-vd-fact">
                  <i className="bi bi-speedometer2" aria-hidden="true"></i>
                  <div>
                    <p>
                      <strong>Distance incluse : {annonces?.distanceOutMin} km</strong>
                    </p>
                    <p className="mc-vd-muted">
                      Frais de 0,13€ pour chaque kilomètre supplémentaire parcouru
                    </p>
                  </div>
                </div>
                <div className="mc-vd-fact">
                  <i className="bi bi-shield-check" aria-hidden="true"></i>
                  <div>
                    <p>
                      <strong>Assurance et protection</strong>
                    </p>
                    <p className="mc-vd-muted">Assurance fournie par AXA France</p>
                  </div>
                </div>
              </div>

              <hr className="mc-vd-divider" />

              {/* Politique d'annulation */}
              <div className="mc-vd-policy">
                <p className={`mc-vd-policy-title is-${policy.tone}`}>
                  <i className={`bi ${policy.icon}`} aria-hidden="true"></i>
                  <span>Politique d'annulation : {policy.label}</span>
                </p>
                <ul>
                  {policy.rules.map((r, i) => (
                    <li key={i}>
                      <i className={`bi ${r.icon} is-${r.tone}`} aria-hidden="true"></i>
                      <span>{r.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mc-vd-actions">
                <button
                  type="button"
                  className="mc-btn mc-btn--secondary mc-btn--block"
                  onClick={handleToggleFavorite}
                >
                  <i
                    className={`bi ${isFavorited ? 'bi-heart-fill' : 'bi-heart'}`}
                    aria-hidden="true"
                  ></i>
                  {isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                </button>
                <button
                  type="button"
                  className="mc-vd-report"
                  data-bs-toggle="modal"
                  data-bs-target="#reportAnnonceModal"
                >
                  <i className="bi bi-flag" aria-hidden="true"></i> Signaler l'annonce
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Carte */}
      {annonces && (
        <div className="container mc-container mc-vd-map">
          <MapItem
            centers={Array(annonces)}
            centercoords={[
              parseFloat(annonces?.locationCoordinates?.split(',')[0]),
              parseFloat(annonces?.locationCoordinates?.split(',')[1]),
            ]}
          ></MapItem>
        </div>
      )}

      {/* Barre mobile */}
      <div className="mc-vd-mobilebar">
        <p className="mc-vd-mobilebar-price">
          {DateIn && DateOut && (
            <>
              {totalPrice}€<small>au total</small>
            </>
          )}
        </p>
        <button
          type="button"
          className="mc-btn mc-btn--primary"
          disabled={!canBook}
          data-bs-toggle="modal"
          data-bs-target="#reservationModalMobile"
        >
          <i className="bi bi-lightning-fill" aria-hidden="true"></i> Envoyer une demande
        </button>
      </div>

      {/* Détail du prix */}
      <div
        className="offcanvas offcanvas-start mc-vd-offcanvas"
        tabIndex="-1"
        id="offcanvasExample"
        aria-labelledby="offcanvasExampleLabel"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasExampleLabel">
            Détails du prix
          </h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Fermer"
          ></button>
        </div>
        <div className="offcanvas-body">
          <dl className="mc-vd-lines">
            <div className="mc-vd-line">
              <dt>
                {CalcluatedPrice} € × {NombreJours + 1} jour
              </dt>
              {Availabilities && <dd>{totalPrice} €</dd>}
            </div>
            <div className="mc-vd-line">
              <dt>Frais de voyage</dt>
              <dd>{fraisVoyage}€</dd>
            </div>
            <div className="mc-vd-line">
              <dt>TVA</dt>
              <dd>{TVA}€</dd>
            </div>
            <div className="mc-vd-line mc-vd-line--total">
              <dt>Total</dt>
              <dd>{fraisVoyage + TVA}€</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Modal signalement */}
      <div
        className="modal fade mc-vd-modal"
        id="reportAnnonceModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-header border-bottom">
              <h6 className="modal-title text-danger">
                <i className="bi bi-flag me-2"></i>Signaler cette annonce
              </h6>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Fermer"
              />
            </div>
            <ReportAnnonceForm annonceId={annonces?.vehiculeAnnonceId} />
          </div>
        </div>
      </div>

      {/* Modal réservation (mobile) */}
      <div
        className="modal fade mc-vd-modal"
        id="reservationModalMobile"
        tabIndex="-1"
        aria-labelledby="reservationModalMobileLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="reservationModalMobileLabel">
                Confirmation de réservation
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Fermer"
              ></button>
            </div>
            <div className="modal-body">
              <label htmlFor="message" className="mc-field-label">
                Vous pouvez envoyer un message au propriétaire et confirmer la demande de
                réservation (facultatif).
              </label>
              <textarea
                className="mc-input"
                id="message"
                rows="4"
                placeholder="Entrez votre message ici..."
                onChange={(e) => setPersonnalisedMessage(e.target.value)}
                value={personnalisedMessage}
              ></textarea>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="mc-btn mc-btn--ghost" data-bs-dismiss="modal">
                Annuler
              </button>
              <button type="button" className="mc-btn mc-btn--primary" onClick={handleContinuer}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default VoitureProfilePage;
